import { useState, useEffect, useCallback } from 'react';
import { emailTemplateService, EmailTemplate, CreateEmailTemplatePayload, UpdateEmailTemplatePayload } from '../../services/email/emailTemplateService';

interface UseEmailTemplatesReturn {
  templates: EmailTemplate[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  refetch: () => void;
  createTemplate: (payload: CreateEmailTemplatePayload) => Promise<void>;
  updateTemplate: (uuid: string, payload: UpdateEmailTemplatePayload) => Promise<void>;
  deleteTemplate: (uuid: string) => Promise<void>;
  getEffectiveTemplate: (eventUuid: string, templateType: string) => Promise<EmailTemplate | null>;
}

interface UseEmailTemplatesParams {
  event_uuid?: string;
  template_type?: string;
  include_org_defaults?: boolean;
  autoFetch?: boolean;
}

export const useEmailTemplates = (params: UseEmailTemplatesParams = {}): UseEmailTemplatesReturn => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { autoFetch = true, ...fetchParams } = params;

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await emailTemplateService.getTemplates(fetchParams);
      setTemplates(response.templates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch email templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [params.event_uuid, params.template_type, params.include_org_defaults]);

  const createTemplate = useCallback(async (payload: CreateEmailTemplatePayload) => {
    setCreating(true);
    setError(null);
    
    try {
      await emailTemplateService.createTemplate(payload);
      await fetchTemplates(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create email template');
      throw err;
    } finally {
      setCreating(false);
    }
  }, [fetchTemplates]);

  const updateTemplate = useCallback(async (uuid: string, payload: UpdateEmailTemplatePayload) => {
    setUpdating(true);
    setError(null);
    
    try {
      await emailTemplateService.updateTemplate(uuid, payload);
      await fetchTemplates(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update email template');
      throw err;
    } finally {
      setUpdating(false);
    }
  }, [fetchTemplates]);

  const deleteTemplate = useCallback(async (uuid: string) => {
    setDeleting(true);
    setError(null);
    
    try {
      await emailTemplateService.deleteTemplate(uuid);
      await fetchTemplates(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete email template');
      throw err;
    } finally {
      setDeleting(false);
    }
  }, [fetchTemplates]);

  const getEffectiveTemplate = useCallback(async (eventUuid: string, templateType: string): Promise<EmailTemplate | null> => {
    try {
      const response = await emailTemplateService.getEffectiveTemplate(eventUuid, templateType);
      return response.template;
    } catch (err) {
      console.error('Failed to get effective template:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchTemplates();
    }
  }, [autoFetch, fetchTemplates]);

  return {
    templates,
    loading,
    error,
    creating,
    updating,
    deleting,
    refetch: fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getEffectiveTemplate,
  };
};